'use client';

import { useState } from 'react';
import Link from 'next/link';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { trackLeadFormSubmission, trackFormStart } from '@/lib/analytics';

// Drata partnership works by deal registration: the owner registers each
// prospect in Drata's partner portal by hand. Everything needed for that goes
// into the /api/contact message body (the notification email), and the consent
// text is recorded verbatim so the registration is backed by an explicit opt-in.
const FORM_NAME = 'Drata demo request (Drata vs Vanta)';
const CONSENT_TEXT =
  'We’re a Drata partner. By submitting, you agree we may share your details with Drata to arrange a demo and partner pricing.';

const SIZES = ['1-50', '51-100', '101-250', '251-750', '750+'];
const FRAMEWORKS = ['SOC 2', 'ISO 27001', 'HIPAA', 'Other'];
const TIMELINES = ['Within 30 days', '1-3 months', '3-6 months', 'Just researching'];

type Errors = Partial<Record<'name' | 'email' | 'company' | 'size' | 'frameworks' | 'timeline' | 'consent', string>>;

export default function DrataLeadForm() {
  const [loadTime] = useState(() => Date.now());
  const [started, setStarted] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [size, setSize] = useState('');
  const [frameworks, setFrameworks] = useState<string[]>([]);
  const [otherFramework, setOtherFramework] = useState('');
  const [timeline, setTimeline] = useState('');
  const [consent, setConsent] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<'idle' | 'submitting' | 'error' | 'success'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [emailSuggestion, setEmailSuggestion] = useState('');

  const onFirstInput = () => {
    if (started) return;
    setStarted(true);
    trackFormStart(FORM_NAME, 'drata_vs_vanta');
  };

  const toggleFramework = (f: string) =>
    setFrameworks((cur) => (cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f]));

  const validate = () => {
    const e: Errors = {};
    if (!name.trim()) e.name = 'Please enter your name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) e.email = 'Please enter a valid work email.';
    if (!company.trim()) e.company = 'Please enter your company.';
    if (!size) e.size = 'Please choose a company size.';
    if (frameworks.length === 0) e.frameworks = 'Choose at least one framework.';
    if (!timeline) e.timeline = 'Please choose a timeline.';
    if (!consent) e.consent = 'We need your consent to share your details with Drata.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (honeypot) {
      setStatus('success');
      return;
    }
    if (!validate()) return;

    setStatus('submitting');
    setErrorMessage('');
    setEmailSuggestion('');

    const parts = name.trim().split(/\s+/);
    const firstName = parts[0];
    const lastName = parts.length > 1 ? parts.slice(1).join(' ') : '';
    const frameworkList = frameworks
      .map((f) => (f === 'Other' && otherFramework.trim() ? `Other: ${otherFramework.trim()}` : f))
      .join(', ');
    const pageUrl = typeof window !== 'undefined' ? window.location.href : '';

    const message = [
      'DRATA DEAL REGISTRATION REQUEST (source: drata-vs-vanta)',
      '',
      `Name: ${name.trim()}`,
      `Work email: ${email.trim()}`,
      `Company: ${company.trim()}`,
      `Company size: ${size} employees`,
      `Target framework(s): ${frameworkList}`,
      `Timeline: ${timeline}`,
      '',
      `Consent to share with Drata: YES (checked ${new Date().toISOString()})`,
      `Consent text shown: "${CONSENT_TEXT}"`,
      `Page: ${pageUrl}`,
    ].join('\n');

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName,
          lastName,
          email: email.trim(),
          company: company.trim(),
          message,
          formName: FORM_NAME,
          src: 'drata-vs-vanta',
          cat: 'compliance',
          pageUrl,
          _t: loadTime,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (typeof data.suggestion === 'string' && data.suggestion) setEmailSuggestion(data.suggestion);
        throw new Error(data.error || 'Failed to submit. Please try again.');
      }
      trackLeadFormSubmission(FORM_NAME, 'drata_vs_vanta', undefined, email.trim());
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMessage(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  };

  if (status === 'success') {
    return (
      <div role="status" className="rounded-2xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-8 text-center">
        <CheckCircle2 className="w-10 h-10 mx-auto text-green-600 dark:text-green-400 mb-3" aria-hidden="true" />
        <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">Thanks, we have your details.</p>
        <p className="text-gray-700 dark:text-gray-300 mt-2">
          We will be in touch within one business day to arrange your Drata demo.
        </p>
      </div>
    );
  }

  const input =
    'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#2cc3d1]';
  const label = 'block text-sm font-medium text-gray-800 dark:text-gray-200 mb-1';
  const err = 'text-sm text-red-600 dark:text-red-400 mt-1';

  return (
    <form onSubmit={handleSubmit} onInput={onFirstInput} noValidate name={FORM_NAME} className="space-y-5">
      {/* Honeypot */}
      <input
        type="text"
        name="website"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
      />

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="dl-name" className={label}>Name</label>
          <input id="dl-name" name="name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} className={input} aria-invalid={!!errors.name} />
          {errors.name && <p className={err}>{errors.name}</p>}
        </div>
        <div>
          <label htmlFor="dl-email" className={label}>Work email</label>
          <input id="dl-email" name="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className={input} aria-invalid={!!errors.email} />
          {errors.email && <p className={err}>{errors.email}</p>}
          {emailSuggestion && (
            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
              Did you mean{' '}
              <button type="button" className="underline" onClick={() => { setEmail(emailSuggestion); setEmailSuggestion(''); setStatus('idle'); }}>
                {emailSuggestion}
              </button>
              ?
            </p>
          )}
        </div>
        <div>
          <label htmlFor="dl-company" className={label}>Company</label>
          <input id="dl-company" name="company" autoComplete="organization" value={company} onChange={(e) => setCompany(e.target.value)} className={input} aria-invalid={!!errors.company} />
          {errors.company && <p className={err}>{errors.company}</p>}
        </div>
        <div>
          <label htmlFor="dl-size" className={label}>Company size</label>
          <select id="dl-size" name="size" value={size} onChange={(e) => setSize(e.target.value)} className={input} aria-invalid={!!errors.size}>
            <option value="">Select…</option>
            {SIZES.map((s) => (
              <option key={s} value={s}>{s} employees</option>
            ))}
          </select>
          {errors.size && <p className={err}>{errors.size}</p>}
        </div>
      </div>

      <fieldset>
        <legend className={label}>Target framework(s)</legend>
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          {FRAMEWORKS.map((f) => (
            <label key={f} className="inline-flex items-center gap-2 text-gray-800 dark:text-gray-200">
              <input type="checkbox" checked={frameworks.includes(f)} onChange={() => toggleFramework(f)} className="h-4 w-4" />
              {f}
            </label>
          ))}
        </div>
        {frameworks.includes('Other') && (
          <input
            aria-label="Other framework"
            placeholder="Which framework?"
            value={otherFramework}
            onChange={(e) => setOtherFramework(e.target.value)}
            className={`${input} mt-2`}
          />
        )}
        {errors.frameworks && <p className={err}>{errors.frameworks}</p>}
      </fieldset>

      <div>
        <label htmlFor="dl-timeline" className={label}>Timeline</label>
        <select id="dl-timeline" name="timeline" value={timeline} onChange={(e) => setTimeline(e.target.value)} className={input} aria-invalid={!!errors.timeline}>
          <option value="">Select…</option>
          {TIMELINES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        {errors.timeline && <p className={err}>{errors.timeline}</p>}
      </div>

      <div>
        <label className="flex items-start gap-3 text-sm text-gray-700 dark:text-gray-300">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="h-4 w-4 mt-0.5" aria-invalid={!!errors.consent} />
          <span>
            {CONSENT_TEXT} See our{' '}
            <Link href="/privacy-policy" className="underline">
              privacy policy
            </Link>
            .
          </span>
        </label>
        {errors.consent && <p className={err}>{errors.consent}</p>}
      </div>

      {status === 'error' && errorMessage && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">{errorMessage}</p>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="inline-flex items-center justify-center gap-2 bg-[#2813e8] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#2010c0] disabled:opacity-60"
      >
        {status === 'submitting' && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
        Request a Drata demo
      </button>
    </form>
  );
}
